import { ReactElement, useCallback, useMemo, useState } from 'react'
import { projectFilesRootGuid } from '@constants'
import { getElementDocuments } from '@domainUtils'
import { VerticalTransmitterList } from '@structuralPlanningComponents'
import { filter } from 'lodash-es'
import { Box, Stack, useTheme } from '@mui/material'
import useResultsSectionOpen from '@editor/pages/Results2/hooks/useResultsSectionOpen'
import { EmptyState } from '@ui2/feedback'
import { Section } from '@ui2/structure'
import { Tabs as DetailTabs } from '@ui/navigation'
import { useSnackbar } from '@ui/snackbar'
import {
  useModelStore,
  useResultsStore,
  useStiffeningStore,
  useTransmissionStore,
} from '@editorStores'
import { useResultsSelection, useAllDomains, useElementType, useProjectFiles } from '@editorHooks'
import { AssemblyAssignmentForm } from '@editorComponents'
import useDesignFormNavigationGuard from '../../hooks/useDesignFormNavigationGuard'
import { useEnhanceLoadsWithDomain } from '../../hooks/useEnhanceLoadsWithDomain'
import useResults2DefaultLoadTab from '../../hooks/useResults2DefaultLoadTab'
import { getStandardRipMemberGuidForWall, getStiffeningSegmentGuidForWall } from '../../utils'
import Results2DetailCloseButton from '../Results2DetailCloseButton'
import { DocumentsTab } from '../Results2DetailPane'
import Results2LoadResults from '../Results2DetailPane/Results2LoadResults'
import type { Results2RelationLink } from '../Results2DetailPane/components/Results2RelationLinksRow'
import type { DetailPaneTabId } from '../Results2DetailPane/types'
import type { Results2ListItem } from '../Results2ElementList'
import Results2PaneHeader from '../Results2PaneHeader'

export type Results2AssemblySurfaceDetailPaneProps = {
  elementId: string
  selectedItem: Results2ListItem
  disableForm?: boolean
}

/** Detail for Decken / Dächer (vertical slabs) and Wand → Wände: Bemessung (loads), Vertikale Verbindungen, Dokumente. */
const Results2AssemblySurfaceDetailPane = ({
  elementId,
  selectedItem,
  disableForm = false,
}: Results2AssemblySurfaceDetailPaneProps): ReactElement => {
  const theme = useTheme()
  const [tab, setTab] = useState<DetailPaneTabId>('design')
  const guardDesignFormNav = useDesignFormNavigationGuard()
  const {
    selectResultsPosition: selectPosition,
    selectResultsStiffeningSegment: selectStiffeningSegment,
    selectedVerticalTransmitter: verticalTransmitter,
    setResultsVerticalTransmitter: setVerticalTransmitter,
  } = useResultsSelection()
  const elementType = useElementType(elementId)

  const domains = useAllDomains()
  const verticalTransmissionGraph = useTransmissionStore(state => state.verticalTransmissionGraph)
  const elementLoads = useResultsStore(state => state.elementLoads)
  const positionLoads = useResultsStore(state => state.positionLoads)
  const loadsSection = useResultsSectionOpen('results2.assembly-surface.loads')
  const assemblySection = useResultsSectionOpen('results2.assembly-surface.assembly')
  const { enqueueSnackbar } = useSnackbar()

  const { data: documentsData, isLoading: isLoadingDocuments } =
    useProjectFiles(projectFilesRootGuid)
  const documents = useMemo(
    () => (elementId ? getElementDocuments(documentsData.files, elementId) : []),
    [documentsData.files, elementId],
  )

  const verticalSlabs = useModelStore(state => state.model.vertical_slabs)
  const verticalRoofSlabs = useModelStore(state => state.model.vertical_roof_slabs)
  const standardRipChecks = useResultsStore(state => state.standardRipChecks)
  const stiffeningIntervals = useStiffeningStore(state => state.stiffeningIntervals)

  const loads = useMemo(
    () => [...(elementLoads || []), ...(positionLoads || [])],
    [elementLoads, positionLoads],
  )

  const slabBeams = useMemo(
    () =>
      verticalSlabs && verticalRoofSlabs
        ? [...verticalSlabs, ...verticalRoofSlabs].map(slab => slab.beam)
        : [],
    [verticalSlabs, verticalRoofSlabs],
  )

  const allLoadsWithDomain = useEnhanceLoadsWithDomain({
    loads,
    domains,
    verticalTransmissionGraph,
    slabBeams,
  })

  const filteredLoads = useMemo(() => {
    if (!elementId) return [] as (AreaLoad | DomainLoad)[]
    // Important: for slabs/roof-slabs we only show loads assigned to the slab itself.
    // Including associated slab-beam loads causes line loads to appear incorrectly.
    return filter(allLoadsWithDomain || [], load => load.element_guid === elementId) as (
      | AreaLoad
      | DomainLoad
    )[]
  }, [allLoadsWithDomain, elementId])

  const defaultLoadTab = useResults2DefaultLoadTab(elementId)

  const isWallAssembly =
    elementType === 'inner_walls' || elementType === 'outer_walls' || selectedItem.type === 'Wand'

  // The assembly options depend on the wall placement (inner/outer), which the element type reflects.
  const wallAssemblyElementType =
    elementType === 'inner_walls' || elementType === 'outer_walls' ? elementType : undefined

  // Foundations have no vertical transmission, so they omit the "Vertikale Verbindungen" tab.
  const isFoundation = elementType === 'foundation' || selectedItem.type === 'Fundament'

  const linkedStandardRipMemberGuid = useMemo(
    () =>
      isWallAssembly ? getStandardRipMemberGuidForWall(standardRipChecks, elementId) : undefined,
    [elementId, isWallAssembly, standardRipChecks],
  )

  const linkedStiffeningSegmentGuid = useMemo(
    () =>
      isWallAssembly ? getStiffeningSegmentGuidForWall(stiffeningIntervals, elementId) : undefined,
    [elementId, isWallAssembly, stiffeningIntervals],
  )

  const guardUnsavedAndNavigate = useCallback(
    (navigate: () => void) => {
      if (guardDesignFormNav()) return
      navigate()
    },
    [guardDesignFormNav],
  )

  const relationLinks = useMemo((): Results2RelationLink[] => {
    if (!isWallAssembly) return []

    const links: Results2RelationLink[] = []
    if (linkedStandardRipMemberGuid) {
      links.push({
        label: 'zur Std-Rippe',
        tooltip: 'Standardrippe auswählen',
        onClick: () =>
          guardUnsavedAndNavigate(() =>
            selectPosition(linkedStandardRipMemberGuid, 'walls', 'standard-rips'),
          ),
      })
    }
    if (linkedStiffeningSegmentGuid) {
      links.push({
        label: 'zur Wandscheibe',
        tooltip: 'Wandscheibe auswählen',
        onClick: () =>
          guardUnsavedAndNavigate(() => selectStiffeningSegment(linkedStiffeningSegmentGuid)),
      })
    }
    return links
  }, [
    guardUnsavedAndNavigate,
    isWallAssembly,
    linkedStandardRipMemberGuid,
    linkedStiffeningSegmentGuid,
    selectPosition,
    selectStiffeningSegment,
  ])

  return (
    <Box
      sx={{
        height: '100%',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        background: theme.palette.common.white,
      }}
    >
      <Box sx={{ flexShrink: 0, position: 'relative' }}>
        <DetailTabs
          items={[
            { id: 'design', label: 'Bemessung' },
            ...(isFoundation
              ? []
              : [{ id: 'vertical-connections', label: 'Vertikale Verbindungen' }]),
            { id: 'documents', label: `Dokumente (${documents.length})` },
          ]}
          value={tab}
          onChange={id => setTab(id as DetailPaneTabId)}
          size="md"
          kind="folder"
        />
        <Results2DetailCloseButton />
      </Box>

      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {tab === 'design' ? (
          <>
            <Results2PaneHeader
              title={selectedItem.label ?? elementId}
              subtitle={`${selectedItem.type} · ${selectedItem.qs}`}
              relationLinks={relationLinks}
            />
            <Stack spacing={3} sx={{ px: 2, py: 1.5 }}>
              {wallAssemblyElementType && !disableForm && (
                <Section title="Aufbau" collapsible hideBorderBottom {...assemblySection}>
                  <AssemblyAssignmentForm
                    elementType={wallAssemblyElementType}
                    selectedElements={[elementId]}
                    showSelectedElementsNumber={false}
                    autoFocus={false}
                    onSaved={() =>
                      enqueueSnackbar(
                        'Der Aufbau wurde geändert. Bitte neu berechnen, damit Lasten und Nachweise aktualisiert werden.',
                        { variant: 'info' },
                      )
                    }
                  />
                </Section>
              )}
              <Section title="Lasten" collapsible {...loadsSection}>
                <Results2LoadResults
                  loads={filteredLoads}
                  defaultLoadTab={defaultLoadTab}
                  elementId={elementId}
                />
              </Section>
            </Stack>
          </>
        ) : tab === 'documents' ? (
          <DocumentsTab
            elementId={elementId}
            documents={documents}
            isLoadingDocuments={isLoadingDocuments}
            disableForm={disableForm}
          />
        ) : elementId ? (
          <Box sx={{ p: 2 }}>
            <VerticalTransmitterList
              selectedElement={elementId}
              verticalTransmitter={verticalTransmitter}
              setVerticalTransmitter={guid => setVerticalTransmitter(guid)}
              disableChanging
            />
          </Box>
        ) : (
          <EmptyState sx={{ py: 2 }}>Kein Bauteil ausgewählt</EmptyState>
        )}
      </Box>
    </Box>
  )
}

export default Results2AssemblySurfaceDetailPane
